import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { useConfigStore } from '../stores/configStore';
import { updateUser } from '../services/userService';
import { colors } from '../styles/colors';

interface Props { onComplete: () => void; }

export default function GoogleProfileSetup({ onComplete }: Props) {
  const { t, i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const user = useAuthStore((s) => s.user);
  const config = useConfigStore((s) => s.config);
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | 'unknown'>('unknown');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;

  const handleSave = async () => {
    if (!nickname.trim()) return;
    setSaving(true);
    setError('');
    try {
      // Check nickname uniqueness
      const { checkNicknameAvailable } = await import('../services/userService');
      const available = await checkNicknameAvailable(nickname.trim(), user.uid);
      if (!available) {
        setError(t('auth.nicknameDuplicate'));
        return;
      }
      await updateUser(user.uid, { nickname: nickname.trim(), gender, profileSetupDone: true } as any);
      useAuthStore.getState().setUser({ ...user, nickname: nickname.trim(), gender, profileSetupDone: true });
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Error');
    } finally { setSaving(false); }
  };

  const nicknameCost = config.nicknameChangeCost ?? 100000;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative z-10 w-full max-w-sm rounded-2xl p-5" style={{ backgroundColor: colors.surface, border: `2px solid ${colors.primary}` }}>
        <h2 className="mb-1 text-center text-lg font-bold" style={{ color: colors.text }}>
          {isKo ? '프로필 설정' : 'Profile Setup'}
        </h2>
        <p className="mb-4 text-center text-xs" style={{ color: colors.textMuted }}>
          {isKo ? '닉네임과 성별을 설정해주세요' : 'Please set your nickname and gender'}
        </p>

        <input type="text" value={nickname} onChange={(e) => setNickname(e.target.value)}
          placeholder={t('auth.nickname')} maxLength={15}
          className="mb-1 w-full rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none"
          style={{ borderColor: colors.border, color: colors.text }} />
        <p className="mb-3 text-[10px]" style={{ color: colors.textMuted }}>
          {isKo
            ? `닉네임 설정 후 변경 시 ${nicknameCost.toLocaleString()}셀이 필요합니다.`
            : `Changing nickname later costs ${nicknameCost.toLocaleString()} cells.`}
        </p>

        <div className="mb-1 flex gap-2">
          {(['male', 'female', 'unknown'] as const).map(g => (
            <button key={g} onClick={() => setGender(g)}
              className="flex-1 rounded-lg py-2 text-center text-xs font-bold"
              style={{
                backgroundColor: gender === g ? (g === 'male' ? '#4a9eff20' : g === 'female' ? '#ff6b9d20' : `${colors.card}`) : colors.card,
                color: gender === g ? (g === 'male' ? '#4a9eff' : g === 'female' ? '#ff6b9d' : colors.text) : colors.textMuted,
                border: `1.5px solid ${gender === g ? (g === 'male' ? '#4a9eff' : g === 'female' ? '#ff6b9d' : colors.border) : 'transparent'}`,
              }}>
              {g === 'male' ? (isKo ? '남성' : 'Male') : g === 'female' ? (isKo ? '여성' : 'Female') : (isKo ? '비공개' : 'Private')}
            </button>
          ))}
        </div>
        <p className="mb-4 text-[10px]" style={{ color: colors.warning }}>
          {isKo ? '성별은 설정 후 변경할 수 없습니다.' : 'Gender cannot be changed after setup.'}
        </p>

        {error && <p className="mb-2 text-center text-xs" style={{ color: colors.error }}>{error}</p>}

        <button onClick={handleSave} disabled={!nickname.trim() || saving}
          className="w-full rounded-lg py-2.5 text-sm font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: colors.primary }}>
          {saving ? '...' : isKo ? '시작하기' : 'Get Started'}
        </button>
      </div>
    </div>
  );
}
